import React from "react";

/**
 * Template #2 — branching_tube (3D Isometric)
 * Explains: Airflow, Blood flow, Trees, Hierarchies, Distribution, Networks
 * Parts: root, branch_left, branch_right, endpoints
 * Actions: draw, highlight, animate_flow, zoom, fade
 *
 * 3D tubular branches with cylindrical depth, lighting, and glow.
 */

export interface BranchingTubeProps {
  visibleParts?: string[];
  highlightTarget?: string;
  fadedParts?: string[];
  opacity?: number;
  flowProgress?: number;
  accentColor?: string;
  labels?: { root?: string; left?: string; right?: string };
}

export const BranchingTube: React.FC<BranchingTubeProps> = ({
  visibleParts,
  highlightTarget,
  fadedParts = [],
  opacity = 1,
  flowProgress,
  accentColor = "#10b981",
  labels = {},
}) => {
  const allVisible = !visibleParts;
  const isVisible = (part: string) => allVisible || visibleParts!.includes(part);
  const isFaded = (part: string) => fadedParts.includes(part);
  const isHighlighted = (part: string) => highlightTarget === part;

  const cx = 400;
  const rootY = 80;
  const splitY = 195;
  const endY = 320;

  const partOpacity = (part: string) => (isFaded(part) ? 0.3 : 1);

  const uid = "bt" + accentColor.replace("#", "");

  // 3D tube: draw a tube with a highlight stripe
  const tube = (
    id: string,
    d: string,
    width: number,
    part: string,
  ) => {
    const hl = isHighlighted(part);
    const color = hl ? accentColor : "#475569";
    const dashProps = flowProgress !== undefined
      ? { strokeDasharray: "10 6", strokeDashoffset: -flowProgress * 100 }
      : {};

    return (
      <g key={id} opacity={partOpacity(part)}>
        {/* Outer shadow */}
        <path d={d} fill="none" stroke="#000" strokeWidth={width + 6} opacity={0.1} strokeLinecap="round" />
        {/* Main tube body */}
        <path d={d} fill="none" stroke={color} strokeWidth={width} strokeLinecap="round" {...dashProps} />
        {/* Inner highlight (specular) */}
        <path d={d} fill="none" stroke="#fff" strokeWidth={Math.max(1, width * 0.3)} opacity={hl ? 0.3 : 0.08} strokeLinecap="round" />
        {/* Glow for highlighted */}
        {hl && (
          <path d={d} fill="none" stroke={accentColor} strokeWidth={width + 14} opacity={0.08} strokeLinecap="round" />
        )}
      </g>
    );
  };

  // 3D opening ring at tube end
  const ring = (rx: number, ry: number, r: number, part: string) => {
    const hl = isHighlighted(part);
    const color = hl ? accentColor : "#475569";
    return (
      <g>
        <ellipse cx={rx} cy={ry} rx={r + 4} ry={r * 0.45 + 2} fill="#0f172a" stroke={color} strokeWidth={2} />
        <ellipse cx={rx} cy={ry} rx={r} ry={r * 0.45} fill="#1e293b" stroke={color} strokeWidth={1.5} />
        {/* Inner glow */}
        <ellipse cx={rx} cy={ry - 1} rx={r * 0.5} ry={r * 0.2} fill="#fff" opacity={hl ? 0.15 : 0.04} />
      </g>
    );
  };

  // Endpoint spheres (3D balls)
  const sphere = (sx: number, sy: number, r: number, hl: boolean) => {
    const gid = `sph-${uid}-${sx}-${sy}`;
    return (
      <g key={gid}>
        <defs>
          <radialGradient id={gid} cx="35%" cy="35%">
            <stop offset="0%" stopColor={hl ? accentColor : "#64748b"} stopOpacity="1" />
            <stop offset="70%" stopColor={hl ? accentColor : "#334155"} stopOpacity="0.8" />
            <stop offset="100%" stopColor="#0f172a" stopOpacity="0.9" />
          </radialGradient>
        </defs>
        <circle cx={sx} cy={sy} r={r + 1} fill="#000" opacity={0.15} />
        <circle cx={sx} cy={sy} r={r} fill={`url(#${gid})`} />
        <circle cx={sx - r * 0.25} cy={sy - r * 0.25} r={r * 0.2} fill="#fff" opacity={0.3} />
      </g>
    );
  };

  return (
    <g opacity={opacity}>
      {/* Root trunk */}
      {isVisible("root") && (
        <g>
          {ring(cx, rootY, 16, "root")}
          {tube("root-tube", `M${cx},${rootY + 8} L${cx},${splitY}`, 10, "root")}
          {labels.root && (
            <text x={cx} y={rootY - 20} fill="#94a3b8" textAnchor="middle" fontSize={12} fontWeight={500} fontFamily="'Inter', system-ui, sans-serif">
              {labels.root}
            </text>
          )}
        </g>
      )}

      {/* Left branch */}
      {isVisible("branch_left") && (
        <g>
          {tube("left-main", `M${cx},${splitY} C${cx - 50},${splitY + 50} ${cx - 130},${splitY + 30} ${cx - 150},${endY - 20}`, 7, "branch_left")}
          {tube("left-sub", `M${cx - 85},${splitY + 42} C${cx - 110},${splitY + 65} ${cx - 175},${splitY + 45} ${cx - 200},${endY - 10}`, 4, "branch_left")}
          {labels.left && (
            <text x={cx - 150} y={endY + 12} fill="#94a3b8" textAnchor="middle" fontSize={11} fontWeight={500} fontFamily="'Inter', system-ui, sans-serif">
              {labels.left}
            </text>
          )}
        </g>
      )}

      {/* Right branch */}
      {isVisible("branch_right") && (
        <g>
          {tube("right-main", `M${cx},${splitY} C${cx + 50},${splitY + 50} ${cx + 130},${splitY + 30} ${cx + 150},${endY - 20}`, 7, "branch_right")}
          {tube("right-sub", `M${cx + 85},${splitY + 42} C${cx + 110},${splitY + 65} ${cx + 175},${splitY + 45} ${cx + 200},${endY - 10}`, 4, "branch_right")}
          {labels.right && (
            <text x={cx + 150} y={endY + 12} fill="#94a3b8" textAnchor="middle" fontSize={11} fontWeight={500} fontFamily="'Inter', system-ui, sans-serif">
              {labels.right}
            </text>
          )}
        </g>
      )}

      {/* Endpoints — 3D spheres */}
      {isVisible("endpoints") && (
        <g opacity={partOpacity("endpoints")}>
          {[
            { x: cx - 150, y: endY - 16 },
            { x: cx - 165, y: endY - 2 },
            { x: cx - 135, y: endY - 4 },
            { x: cx - 200, y: endY - 6 },
            { x: cx - 188, y: endY + 6 },
            { x: cx + 150, y: endY - 16 },
            { x: cx + 165, y: endY - 2 },
            { x: cx + 135, y: endY - 4 },
            { x: cx + 200, y: endY - 6 },
            { x: cx + 188, y: endY + 6 },
          ].map((p) => sphere(p.x, p.y, 5, isHighlighted("endpoints")))}
        </g>
      )}
    </g>
  );
};
